import { useCallback, useMemo, useState, type ReactNode } from "react";
import { useT, type Uebersetzer } from "../../i18n";
import { Blattvonunten } from "../../bausteine/Blattvonunten";
import { Feiertagshinweis, useFeiertagAm } from "../../bausteine/Feiertage";
import { alsTag, zweistellig } from "../../daten/kalender";
import { heuteTag, tagKurz, tagPlus, wochentagNr } from "../../daten/nachschlagen";
/* Die Stile des Griffs und des Blattes (`hw-griff`, `hz-*`) wohnen in den
   Telefonregeln des Nachschlagens. **Seit dem 20.09.2026 kommen sie von
   hier mit:** Der Kalender geht jetzt aus jedem Datumsfeld der Anwendung auf
   (`bausteine/Zeitraum.tsx`) — aus der Übergabe, dem Angebot, der Belegsuche
   —, und keine dieser Masken hat vorher im Nachschlagen die Stile geladen. */
import "../../stile/telefon/nachschlagen.css";

/* ══════════════════════════════════════════════════════════════════
   Der Zeitraum am Telefon — ab wann, bis wann, im Kalender getippt.

   Luca, 18.09.2026, nach dem Durchklicken der Handy-Ansicht:

   > *„Zeitraum: … es geht sofort in die Tastatur, und wir wollen
   > natürlich Zeitraum wählen können: 1 Tag, 3 Tage, 7 Tage, 14 Tage —
   > den Scheiß brauchen wir nicht, sondern wir wollen einfach über den
   > Zeitraum den Datepicker machen. Das macht es einfacher. Heute und
   > morgen brauchen wir auch erst mal nicht. Wir wollen: ab wann, bis
   > wann."*

   Und dazu, über jeden Kalender in der Anwendung:

   > *„Wir wollen nur den Datepicker, auch in so einer Variante, sodass
   > wir für die Monate nicht durchblättern müssen. Das macht keinen Sinn.
   > … auf jeden Fall nicht die Tastatur triggern, weil das einen verrückt
   > macht."*

   ── Was daraufhin verschwunden ist ────────────────────────────────

   | weg | warum |
   |---|---|
   | das Tagefeld (`type="number"`) und das Datumsfeld | Beide holten die Tastatur. Das Feld war der einzige Grund, warum das Blatt beim Aufgehen etwas fokussieren konnte |
   | „Heute" und „Morgen" | Zwei Tage von 365, und der Kalender steht ohnehin auf dem laufenden Monat |
   | „1 Tag · 3 Tage · 1 Woche · 2 Wochen" | Eine Dauer ist das Ergebnis, nicht die Frage. Wer bis Freitag mietet, weiß den Freitag und nicht die Zahl |
   | die Pfeile ‹ HEUTE › über einem Monat | Ein Blättern, bei dem man den Monat verliert, den man sucht |

   ── Was statt dessen dasteht ──────────────────────────────────────

   Der Aufbau ist der, den die Reisebuchung am Telefon seit Jahren führt
   (Agoda, Polarsteps, Vestiaire) — vier Teile, von oben nach unten:

   1. **Die beiden Tage nebeneinander, mit einem Pfeil dazwischen**: „ab
      wann → bis wann". Es sind **Anzeigen, keine Felder** — damit ist die
      Tastatur an der Wurzel weg und nicht bloß beim Aufgehen.
   2. **Die Wochentagszeile bleibt oben stehen**, während die Monate laufen.
      Ohne sie zählt man in der dritten Woche wieder ab, welche Spalte der
      Freitag ist.
   3. **Die Monate untereinander in einer Rollfläche**, beginnend beim
      laufenden — der Monatsname als Zwischenüberschrift. **Zwölf Monate**,
      also ein Jahr: Weiter voraus wird am Telefon nichts vermietet, und
      eine Liste, die endlos weiterrollt, sagt einem nie, dass sie zu Ende
      ist. Wer darüber hinaus plant, sitzt am Schreibtisch
      (`bausteine/Zeitraum.tsx`).
   4. **Unten der Knopf, der die abgeleitete Zahl trägt** — „Übernehmen ·
      3 Tage". Genau das ersetzt die vier Dauerstufen: Die Zahl ist weiter
      da, sie wird nur nicht mehr eingegeben.

   **Es sind Kalendertage, und sie heißen auch so.** Wie viele davon
   Miettage sind, hängt am Mietkalender von Typ, Gruppe und Betrieb — das
   weiß nur die andere Seite (`GET /preise`), und es steht an der
   Trefferzeile darunter. Hier „Miettage" zu schreiben wäre eine zweite,
   geratene Wahrheit (Regel 6: Belegung ≠ Berechnung).

   **Der erste Tipper setzt den Anfang, der zweite das Ende**, und die Spanne
   dazwischen steht sichtbar. Ein Tipper vor den Anfang beginnt von vorn —
   wer rückwärts tippt, meint einen neuen Anfang und kein Ende davor.

   **Die Tage sind abgeleitet, nicht eingegeben.** Wie viele es sind, steht
   im Kopf des Blattes und im Griff darunter. Es sind **Kalendertage**; wie
   viele davon Miettage sind, rechnet die andere Seite und sagt es an der
   Trefferzeile (Regel 6: Belegung ≠ Berechnung).

   **Vergangene Tage sind gesperrt.** Ein Zeitraum, der gestern anfängt,
   beantwortet keine Frage, die jemand am Telefon stellt. Wer zurückblickt
   — die Belegsuche, eine Anschaffung —, sagt es mit `ab`, dem ersten
   wählbaren Tag; die Liste beginnt dann dort und reicht bis heute.

   **Seit dem 20.09.2026 ist das hier der Kalender jedes Datumsfelds am
   Telefon** — `bausteine/Zeitraum.tsx` öffnet `Zeitraumblatt` und
   `Tagesblatt` statt seines schwebenden Blatts, weil dessen Textfeld die
   Tastatur holte. Die Stile kommen deshalb von hier mit (unten).

   Klassen: `hz-*` (neu) und die geteilten `hw-griff`/`hw-tat`, gegen alle
   Stile geprüft.
   ══════════════════════════════════════════════════════════════════ */

/** So weit reicht die Liste: der laufende Monat und elf weitere. */
const MONATE = 12;

/** `2026-09-19` → ein Datum in der Uhr des Geräts. */
function ausIso(iso: string): Date {
  const [j, m, t] = iso.split("-").map(Number);
  return new Date(j ?? 1970, (m ?? 1) - 1, t ?? 1);
}

type Monatsstand = { jahr: number; monat: number };

/** Der Monat, in dem ein Tag liegt — als Zahl, damit sich Monate vergleichen lassen. */
const monatsZahl = (iso: string): number => {
  const d = ausIso(iso);
  return d.getFullYear() * 12 + d.getMonth();
};

/**
 * Die Monate der Liste — vom ersten bis zum letzten, den jemand sehen muss.
 *
 * **Zwölf ab dem ersten wählbaren Tag** ist die Regel (oben). Dazu kommen
 * die Monate, die ein Zeitraum sonst nicht zeigen könnte: der des gewählten
 * Tages, wenn er außerhalb liegt (eine Frist, die schon abgelaufen ist; eine
 * Anschaffung vor zwei Jahren), und der laufende — wer ein Jahr zurückblickt,
 * soll bei heute ankommen und nicht einen Monat davor enden.
 */
function monateAb(ab: string, ...mitDrin: (string | null)[]): Monatsstand[] {
  const heute = heuteTag();
  const zahlen = [ab, heute, ...mitDrin].filter((x): x is string => Boolean(x)).map(monatsZahl);
  const erster = Math.min(...zahlen);
  const letzter = Math.max(erster + MONATE - 1, ...zahlen);
  return Array.from({ length: letzter - erster + 1 }, (_, i) => {
    const z = erster + i;
    return { jahr: Math.floor(z / 12), monat: z % 12 };
  });
}

/**
 * Beim Aufgehen steht der Monat des gewählten Tages im Bild. Ohne das beginnt
 * die Liste beim ersten Monat, auch wenn der gewählte Zeitraum drei Monate
 * weiter liegt — und der Zeitraum, den man ändern will, wäre unsichtbar.
 *
 * Der festsitzende Kopf zählt dabei ab: Er liegt über der Rollfläche, und ein
 * Monatsname genau darunter wäre zugedeckt.
 *
 * **Ein Ref-Rückruf, kein Effekt** (20.09.2026). Das Blatt von unten hängt
 * sich erst nach seinem ersten Durchlauf in den Arbeitsbereich
 * (`Blattvonunten`, `ziel`); ein Effekt hier lief davor und fand keine Liste
 * — gemessen: Der gewählte Monat stand 544 px unter der Oberkante, nie im
 * Bild. Der Rückruf kommt, sobald die Liste im Baum steht, und noch einmal,
 * wenn sich der Tag ändert.
 */
function useZumMonat(iso: string): (el: HTMLDivElement | null) => void {
  return useCallback((liste: HTMLDivElement | null) => {
    const ziel = liste?.querySelector<HTMLElement>(`[data-monat="${monatsschluessel(iso)}"]`);
    const rollt = liste?.closest<HTMLElement>(".bvu-leib");
    if (!liste || !ziel || !rollt) return;
    const fest = liste.querySelector<HTMLElement>(".hz-fest")?.offsetHeight ?? 0;
    rollt.scrollTop +=
      ziel.getBoundingClientRect().top - rollt.getBoundingClientRect().top - fest;
  }, [iso]);
}

/** „Mi 17.9." — Wochentag und Tag, ohne Jahr. */
export function tagMitWochentag(t: Uebersetzer, iso: string): string {
  const tag = t.liste("kalender.wochentageKurz")[wochentagNr(iso)] ?? "";
  return t("kalender.tagMitDatum", { tag, datum: tagKurz(iso) });
}

/** „{zahl} Tage", und „1 Tag" ist keine Aufzählung von einem. */
export const tageText = (t: Uebersetzer, tage: number): string =>
  tage === 1 ? t("nachschlagen.einTag") : t("nachschlagen.tageZahl", { zahl: tage });

/** Der letzte Tag des Zeitraums — beide Ränder zählen mit. */
export const bisTag = (von: string, tage: number): string => tagPlus(von, Math.max(1, tage) - 1);

/** Wie viele Kalendertage zwischen zwei Tagen liegen, beide mitgezählt. */
export function tageZwischen(von: string, bis: string): number {
  const spanne = ausIso(bis).getTime() - ausIso(von).getTime();
  return Math.max(1, Math.round(spanne / 86_400_000) + 1);
}

/** „Sa 19.9. – Mo 21.9. · 3 Tage" — die Zeile, die im Griff steht. */
export const zeitraumText = (t: Uebersetzer, von: string, tage: number): string =>
  t("nachschlagen.zeitraumWert", {
    von: tagMitWochentag(t, von),
    bis: tagMitWochentag(t, bisTag(von, tage)),
    dauer: tageText(t, tage),
  });

/* ══════════════ Ein Monat ══════════════ */

/** Der Schlüssel, unter dem ein Monatsblock im DOM steht. */
const monatsschluessel = (iso: string): string => iso.slice(0, 7);

function Monat({ jahr, monat, anfang, ende, heute, tippen, ab = heute }: {
  jahr: number;
  monat: number;
  anfang: string;
  ende: string | null;
  heute: string;
  tippen: (iso: string) => void;
  /** Der erste Tag, der sich tippen lässt — ohne Angabe heute. */
  ab?: string;
}) {
  const t = useT();
  const monatsnamen = t.liste("kalender.monate");
  /* Feiertage markiert, der Name im `title` (0287). Die Trefferfläche
     bleibt, wie sie ist — nur Klasse und Titel kommen dazu. */
  const feiertagAm = useFeiertagAm();
  const erster = new Date(jahr, monat, 1);
  /* Der Vorlauf bis zum Ersten — Montag ist die erste Spalte. Er steht als
     leere Zelle da und nicht als Tag des Vormonats: Ein Tag, der zweimal in
     der Liste vorkommt, ist am Telefon ein Fehlgriff. */
  const vorlauf = (erster.getDay() + 6) % 7;
  const laenge = new Date(jahr, monat + 1, 0).getDate();

  return (
    <section className="hz-monat" data-monat={`${jahr}-${zweistellig(monat + 1)}`}>
      <h4 className="hz-monat-kopf">
        {t("kalender.monatJahr", { monat: monatsnamen[monat] ?? "", jahr })}
      </h4>
      <div className="hz-gitter">
        {Array.from({ length: vorlauf }, (_, i) => (
          <span className="hz-luecke" key={`l${i}`} aria-hidden="true" />
        ))}
        {Array.from({ length: laenge }, (_, i) => {
          const iso = alsTag(new Date(jahr, monat, i + 1));
          const vorbei = iso < ab;
          const rand = iso === anfang || (ende !== null && iso === ende);
          const drin = ende !== null && iso > anfang && iso < ende;
          const klassen = ["hz-tag"];
          if (rand) klassen.push("rand");
          /* Anfang und Ende schließen den Balken nur an, wenn es einen gibt:
             Solange erst der Anfang steht, ist er ein Punkt und kein
             halber Strich ins Leere. */
          if (ende !== null) {
            if (drin) klassen.push("drin");
            if (iso === anfang) klassen.push("erst");
            if (iso === ende) klassen.push("letzt");
          }
          if (iso === heute) klassen.push("heute");
          const feiertag = feiertagAm(iso);
          if (feiertag) klassen.push("feiertag");
          return (
            <button
              key={iso}
              type="button"
              className={klassen.join(" ")}
              disabled={vorbei}
              aria-label={feiertag ? `${iso} ${feiertag}` : iso}
              {...(feiertag ? { title: feiertag } : {})}
              {...(rand ? { "aria-pressed": true as const } : {})}
              onClick={() => tippen(iso)}
            >
              {i + 1}
            </button>
          );
        })}
      </div>
    </section>
  );
}

/* ══════════════ Das Blatt ══════════════ */

/**
 * **Seit dem 20.09.2026 auch der Zeitraum des Schreibtischs am Telefon.**
 * Luca, über die Position im Angebot: *„Der Date Picker ist sowieso an dieser
 * Stelle sehr, sehr schlecht eingebettet und macht da dann einfach … das als
 * Bottom Sheet oder so."* `bausteine/Zeitraum.tsx` öffnet am Telefon dieses
 * Blatt und nicht mehr sein schwebendes Kalenderblatt — deshalb ist es
 * ausgeführt (`export`) und kennt zwei Dinge mehr: einen eigenen Titel und
 * `ab`, den ersten wählbaren Tag. Die Belegsuche blickt zurück; dort ist
 * „vergangen ist gesperrt" falsch, und `ab` liegt ein Jahr vor heute.
 */
/**
 * Wie weit das Blatt ohne eigenes `ab` zurückreicht — in Tagen.
 *
 * **Nachträglich erfassen** (Prüfrunde Interview F, F20-Rest, 05.10.2026):
 * Bis hierher war im Blatt jeder Tag vor heute gesperrt. Ein Auftrag, der
 * nachträglich erfasst wird (der Bagger ist seit gestern draußen), ließ sich
 * am Telefon weder in „Vermieten" noch im Angebot eintragen — die Rückfrage
 * „nachträglich erfassen?" (F20) war nur über ein älteres Angebot erreichbar.
 * Jetzt reicht es einen Monat zurück, wie der Kalender am Schreibtisch, der
 * nie gesperrt hat; ob ein Beginn in der Vergangenheit gewollt ist, fragt die
 * Annahme. Eine Bedienungsgrenze, keine Betriebszahl.
 */
export const NACHTRAEGLICH_TAGE = 31;

/** Der erste Tag, der sich tippen lässt: das `ab` des Aufrufers, sonst {@link NACHTRAEGLICH_TAGE} vor heute. */
export function ersterWaehlbarerTag(ab: string | undefined, heute: string): string {
  return ab ?? tagPlus(heute, -NACHTRAEGLICH_TAGE);
}

export function Zeitraumblatt({ von, bis, titel, unter, ab, schliessen, uebernehmen }: {
  von: string;
  bis: string;
  /** Der Titel im Kopf — ohne ihn „Zeitraum". */
  titel?: string | undefined;
  /** Die zweite Zeile im Kopf. */
  unter?: string | undefined;
  /** Der erste Tag, der sich tippen lässt — ohne Angabe ein Monat vor heute (F20). */
  ab?: string | undefined;
  schliessen: () => void;
  uebernehmen: (von: string, bis: string) => void;
}) {
  const t = useT();
  const heute = heuteTag();
  const erster = ersterWaehlbarerTag(ab, heute);
  const [anfang, setzeAnfang] = useState(von);
  const [ende, setzeEnde] = useState<string | null>(bis);

  const monate = useMemo(() => monateAb(erster, von, bis), [erster, von, bis]);
  const liste = useZumMonat(von);

  const tage = ende === null ? 1 : tageZwischen(anfang, ende);

  const tippen = (iso: string) => {
    // Steht schon eine Spanne, oder liegt der Tipper vor dem Anfang, dann
    // ist er ein neuer Anfang — und kein Ende vor dem Anfang.
    if (ende !== null || iso < anfang) {
      setzeAnfang(iso);
      setzeEnde(null);
      return;
    }
    setzeEnde(iso);
    uebernehmen(anfang, iso);
  };

  const wochentage = t.liste("kalender.wochentageKurz");

  return (
    <Blattvonunten
      offen
      schliessen={schliessen}
      /* **Der eine Rastpunkt `gross` in dieser Anwendung** (18.09.2026). Jedes
         andere Blatt von unten bleibt klein und lässt die Maske darunter
         stehen; ein Kalender kann das nicht. Unter dem gewählten Zeitraum und
         der Wochentagszeile, die beide oben stehen bleiben, braucht ein Monat
         sechs Reihen — bei `klein` wären davon drei zu sehen, und man
         scrollte an einem Monat entlang, statt ihn anzusehen. Luca hat genau
         diesen Teil gelobt (*„Ich kann jetzt relativ gut Zeiträume
         auswählen"*); kleiner wäre hier ein Rückschritt. */
      rast="gross"
      titel={titel ?? t("nachschlagen.zeitraum")}
      unter={ende === null ? t("nachschlagen.endeTippen") : unter}
      fuss={(
        <button
          type="button"
          className="hw-tat"
          onClick={() => uebernehmen(anfang, ende ?? anfang)}
        >
          {t("nachschlagen.uebernehmenTage", { dauer: tageText(t, tage) })}
        </button>
      )}
    >
      <div className="hz" ref={liste}>
        {/* Beides bleibt oben stehen, während die Monate laufen: die
            gewählte Spanne, damit man sie beim Rollen nicht verliert, und
            die Wochentagszeile, weil man sonst in der dritten Woche wieder
            abzählt, welche Spalte der Freitag ist. */}
        <div className="hz-fest">
          {/* **Die beiden Tage nebeneinander, nicht untereinander.** Ein
              Zeitraum ist eine Strecke; zwei Zeilen daraus zu machen hieße,
              den Pfeil wegzulassen, der ihn erst zu einer macht. Angezeigt,
              nicht eingegeben — hier wird nichts getippt. */}
          <div className="hz-spanne">
            <span className="hz-ecke fest">
              <span className="hz-ecke-schild">{t("nachschlagen.ab")}</span>
              <b className="hz-ecke-tag">{tagMitWochentag(t, anfang)}</b>
            </span>
            <span className="hz-pfeil" aria-hidden="true" />
            <span className={`hz-ecke${ende === null ? " dran" : " fest"}`}>
              <span className="hz-ecke-schild">{t("nachschlagen.bis")}</span>
              <b className="hz-ecke-tag">
                {ende === null ? t("allgemein.strich") : tagMitWochentag(t, ende)}
              </b>
            </span>
          </div>
          {/* Ein Feiertag in der gewählten Spanne — leise, ohne Punkt (0287).
              Nicht beim Rückblick einer Suche (`ab`): Dort ist er keine
              Auskunft über eine Miete. */}
          {ab === undefined
            ? <Feiertagshinweis von={anfang} bis={ende ?? anfang} miettag klasse="hz-feiertag" />
            : null}
          <div className="hz-wochentage" aria-hidden="true">
            {wochentage.map((w, i) => <span key={`wt${i}`}>{w}</span>)}
          </div>
        </div>

        {monate.map((m) => (
          <Monat
            key={`${m.jahr}-${m.monat}`}
            jahr={m.jahr}
            monat={m.monat}
            anfang={anfang}
            ende={ende}
            heute={heute}
            ab={erster}
            tippen={tippen}
          />
        ))}
      </div>
    </Blattvonunten>
  );
}

/* ══════════════ Ein einzelner Tag ══════════════

   Luca, 19.09.2026, über die Wiedervorlage in der Notiz: *„…und dann, wenn
   du sagst ‚Benutzerdefiniertes Datum', dass dann ein Date Picker hochgeht,
   wo ich dann das Datum auswählen kann."*

   **Derselbe Kalender wie beim Zeitraum**, nur mit einem Tipper statt
   zweien: die Monate untereinander, die Wochentagszeile oben stehend, der
   gewählte Tag als Punkt. **Der Tipper ist die Wahl** — ein „Übernehmen"
   darunter wäre ein zweiter Griff für dieselbe Entscheidung. */

export function Tagesblatt({ titel, unter, tag, ab, fuss, schliessen, waehlen }: {
  titel: string;
  /** Die zweite Zeile im Kopf — der Zusammenhang, nicht eine Erklärung. */
  unter?: string | undefined;
  /** Der gewählte Tag, falls schon einer steht. */
  tag: string | null;
  /** Der erste Tag, der sich wählen lässt. */
  ab: string;
  /**
   * Was unten stehen bleibt, während die Monate laufen — der ausdrückliche
   * Weg zum Tippen (`bausteine/Zeitraum.tsx`, `Datumsblatt`). Die Wahl im
   * Kalender braucht keinen Fuß: Der Tipper ist die Wahl.
   */
  fuss?: ReactNode;
  schliessen: () => void;
  waehlen: (iso: string) => void;
}) {
  const t = useT();
  const heute = heuteTag();
  /* Zwölf Monate ab dem ersten wählbaren Tag — und dazu der Monat des
     gewählten, wenn er außerhalb liegt: Eine Frist, die abgelaufen ist,
     soll man sehen, bevor man sie neu setzt. */
  const monate = useMemo(() => monateAb(ab, tag), [ab, tag]);
  const liste = useZumMonat(tag ?? (ab > heute ? ab : heute));
  const wochentage = t.liste("kalender.wochentageKurz");

  return (
    <Blattvonunten offen schliessen={schliessen} rast="gross" titel={titel} unter={unter} fuss={fuss}>
      <div className="hz" ref={liste}>
        <div className="hz-fest">
          <div className="hz-wochentage" aria-hidden="true">
            {wochentage.map((w, i) => <span key={`wt${i}`}>{w}</span>)}
          </div>
        </div>
        {monate.map((m) => (
          <Monat
            key={`${m.jahr}-${m.monat}`}
            jahr={m.jahr}
            monat={m.monat}
            anfang={tag ?? ""}
            ende={null}
            heute={heute}
            ab={ab}
            tippen={waehlen}
          />
        ))}
      </div>
    </Blattvonunten>
  );
}

/* ══════════════ Der Griff ══════════════ */

/**
 * Der Zeitraum als eine Zeile, die sich öffnet.
 *
 * **Nach außen bleibt es „ab wann, wie viele Tage"** — die Maske darüber
 * rechnet mit `von` und `tage`, und `GET /nachschlagen` bekommt daraus sein
 * Fenster. Gewählt wird trotzdem ab wann **bis wann**: Beides ist dieselbe
 * Angabe, und die Zahl der Tage ist die, die niemand im Kopf ausrechnen soll.
 */
export function Zeitraumwahl({ von, tage, aendern }: {
  von: string;
  tage: number;
  aendern: (neu: { von: string; tage: number }) => void;
}) {
  const t = useT();
  const [offen, setzeOffen] = useState(false);

  return (
    <>
      <button type="button" className="hw-griff" onClick={() => setzeOffen(true)}>
        <span className="hw-griff-schild">{t("nachschlagen.zeitraum")}</span>
        <span className="hw-griff-wert">{zeitraumText(t, von, tage)}</span>
        <span className="hw-griff-pfeil" aria-hidden="true" />
      </button>

      {/* Erst beim Öffnen gebaut: Der Entwurf im Blatt beginnt damit jedes
          Mal bei dem, was gerade gilt, statt bei dem, was beim letzten Mal
          halb getippt war. */}
      {offen ? (
        <Zeitraumblatt
          von={von}
          bis={bisTag(von, tage)}
          schliessen={() => setzeOffen(false)}
          uebernehmen={(neuVon, neuBis) => {
            aendern({ von: neuVon, tage: tageZwischen(neuVon, neuBis) });
            setzeOffen(false);
          }}
        />
      ) : null}
    </>
  );
}
